import { Dispatch, useEffect, useRef, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import useReportParams from './useReportParams';
import useSubmitReport from './useSubmitReport';
import { REPORT_KIND_LABELS } from '../model/constants';
import { createInitialState, ReportRequestAction } from '../model/reducer';
import { FormMode, ReportRequestState } from '../model/types';

export interface Submission {
  uid: string;
  projectName?: string;
  kindLabel: string | null;
}

interface Viewer {
  id: number;
  email: string;
  name: string | null;
}

interface Options {
  mode: FormMode;
  state: ReportRequestState;
  dispatch: Dispatch<ReportRequestAction>;
  viewer: Viewer | null | undefined;
  environment: SensoryLinkEnvironment | null;
  /** False while the template or the viewer is still loading. */
  ready: boolean;
  onSubmitted: () => void;
}

/**
 * Submit flow for the single-page form: confirm, send, then keep the answers and
 * rotate the report UID so a re-submit is a new report.
 */
const useFormSubmission = ({
  mode,
  state,
  dispatch,
  viewer,
  environment,
  ready,
  onSubmitted,
}: Options) => {
  const { submit, submitting } = useSubmitReport(viewer?.id);
  const [reportUid, setReportUid] = useState(() => uuidv4());
  // Bumped by "Start a new request" so the form's sections remount with fresh local state.
  const [formVersion, setFormVersion] = useState(0);
  const [attempted, setAttempted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [submission, setSubmission] = useState<Submission | null>(null);
  const { params, valid, issues } = useReportParams(
    state,
    viewer,
    environment,
    reportUid,
  );

  // A different report type brings fresh, untouched sections; don't flag them straight away.
  const kindRef = useRef(state.reportKind);
  useEffect(() => {
    if (kindRef.current === state.reportKind) return;
    kindRef.current = state.reportKind;
    setAttempted(false);
  }, [state.reportKind]);

  // Submit is only rendered once the form is valid.
  const requestSubmit = () => setConfirmOpen(true);

  const confirm = async () => {
    setConfirmOpen(false);
    if (!params || !valid || !ready) return;
    const result = await submit(params);
    if ('reason' in result && result.reason === 'busy') return;
    if ('message' in result) {
      setFailure(result.message);
      return;
    }
    setSubmission({
      uid: reportUid,
      projectName: params.project_name,
      kindLabel: state.reportKind ? REPORT_KIND_LABELS[state.reportKind] : null,
    });
    // The answers stay; a fresh UID makes any further submission a new report.
    setReportUid(uuidv4());
    setAttempted(false);
    onSubmitted();
  };

  const startNew = () => {
    setFormVersion((v) => v + 1);
    dispatch({ type: 'replace', state: createInitialState(mode) });
    setReportUid(uuidv4());
    setSubmission(null);
    setAttempted(false);
  };

  return {
    params,
    paramsValid: valid,
    schemaIssues: issues,
    submitting,
    attempted,
    /** Show every field error without submitting, e.g. when jumping to an incomplete section. */
    revealErrors: () => setAttempted(true),
    confirmOpen,
    cancelConfirm: () => setConfirmOpen(false),
    requestSubmit,
    confirm,
    failure,
    clearFailure: () => setFailure(null),
    submission,
    dismissSubmission: () => setSubmission(null),
    startNew,
    formVersion,
  };
};

export default useFormSubmission;
